import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  TextField,
  MenuItem,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  PlayArrow as PlayArrowIcon,
  RequestQuote as RequestQuoteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOrder } from '../hooks/useOrder';
import PageContainer from '../components/common/PageContainer';
import SectionHeader from '../components/common/SectionHeader';
import StatusChip from '../components/common/StatusChip';
import OrderTraceback from '../components/orders/OrderTraceback';
import ProductionItemTable from '../components/orders/ProductionItemTable';

const ProductionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { logout } = useAuth();
  const {
    order, loading, error, success, setError, setSuccess,
    fetchOrder, updateItem, startProduction, validateResponsible
  } = useOrder();

  const [actualWeights, setActualWeights] = useState({});
  const [responsible, setResponsible] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (id) {
      fetchOrder(id);
      fetchActiveUsers();
    }
  }, [id, fetchOrder]);

  useEffect(() => {
    if (order?.items) {
      const weights = {};
      order.items.forEach(item => {
        if (item.actual_weight) weights[item.id] = item.actual_weight.toString();
      });
      setActualWeights(weights);
    }
  }, [order]);

  const fetchActiveUsers = async () => {
    try {
      const response = await axios.get('/api/users/?is_active=true');
      setUsers(response.data.results || response.data);
    } catch (e) { }
  };

  const handleWeightChange = (itemId, value) => {
    setActualWeights(prev => ({ ...prev, [itemId]: value }));
  };

  const handleWeightBlur = async (itemId, value) => {
    if (value && !isNaN(parseFloat(value))) {
      await updateItem(itemId, { actual_weight: parseFloat(value) });
    } else if (value === '') {
      await updateItem(itemId, { actual_weight: null });
    }
  };

  const handleFinish = async () => {
    if (!responsible || !password) {
      setError('Complete responsable y contraseña');
      return;
    }

    setSaving(true);
    try {
      const valid = await validateResponsible(responsible, password);
      if (valid) {
        await axios.patch(`/api/orders/${id}/`, { status: 'listo' });
        setSuccess('Pedido finalizado');
        setTimeout(() => navigate('/production'), 2000);
      }
    } catch (e) {
      setError('Error al finalizar el pedido');
    } finally {
      setSaving(false);
    }
  };

  const isReadOnly = order?.status === 'listo' || order?.status === 'facturado' || order?.status === 'entregado';
  
  const unweighedItemsCount = order?.items?.filter(item => {
    const weight = actualWeights[item.id];
    return !weight || weight.toString().trim() === '' || isNaN(parseFloat(weight));
  }).length || 0;

  if (!order && !loading) return <PageContainer onLogout={logout}><Alert severity="error">Pedido no encontrado</Alert></PageContainer>;

  return (
    <PageContainer loading={loading} onLogout={() => { logout(); navigate('/login'); }}>
      <SectionHeader
        title={`Detalles del Pedido #${order?.order_number}`}
        subtitle={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ color: '#4e7397' }}>
              {order?.client ? `Cliente: ${order.client.name}` : "Consumidor Final"}
            </Typography>
            {order && (
              <Chip
                label={order.order_type === 'wholesale' ? 'Negocio' : 'Público'}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  bgcolor: order.order_type === 'wholesale' ? '#ede7f6' : '#e0f7fa',
                  color: order.order_type === 'wholesale' ? '#5e35b1' : '#00838f',
                  borderRadius: '4px'
                }}
              />
            )}
          </Box>
        }
        breadcrumbs={[
          { label: 'Producción', path: '/production' },
          { label: `Pedido ${order?.order_number}`, path: '#' }
        ]}
        actions={[
          { label: 'Volver', variant: 'outlined', icon: <ArrowBackIcon />, onClick: () => navigate('/production') },
          { label: 'Editar Pedido', variant: 'outlined', icon: <EditIcon />, onClick: () => navigate(`/orders/${id}/review`) }
        ]}
      />

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {!isReadOnly && order?.status === 'en_elaboracion' && unweighedItemsCount > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: '0.5rem', fontWeight: 600 }}>
          Quedan {unweighedItemsCount} producto(s) por pesar. Complete todos los pesajes antes de finalizar el pedido.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {order?.status === 'listo' && (
            <Paper sx={{ p: 3, mb: 3, border: '2px dashed #00838f', bgcolor: '#e0f7fa', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <RequestQuoteIcon sx={{ color: '#00838f' }} />
                <Typography sx={{ fontWeight: 700, color: '#00838f' }}>Pedido pendiente de facturación</Typography>
              </Box>
              <Button variant="contained" onClick={() => navigate(`/billing/${id}`)} sx={{ bgcolor: '#00838f', '&:hover': { bgcolor: '#006064' } }}>
                Facturar Pedido
              </Button>
            </Paper>
          )}

          {order?.status === 'confirmado' && (
            <Paper sx={{ p: 3, mb: 3, border: '2px dashed #1976d2', bgcolor: '#e3f2fd', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PlayArrowIcon sx={{ color: '#1976d2' }} />
                <Typography sx={{ fontWeight: 700, color: '#1976d2' }}>Pedido a la espera</Typography>
              </Box>
              <Button variant="contained" onClick={startProduction} disabled={saving}>Iniciar Elaboración</Button>
            </Paper>
          )}

          <ProductionItemTable
            items={order?.items || []}
            actualWeights={actualWeights}
            onWeightChange={handleWeightChange}
            onWeightBlur={handleWeightBlur}
            isReadOnly={isReadOnly}
          />

          {!isReadOnly && order?.status === 'en_elaboracion' && (
            <Paper sx={{ p: 3, mt: 3, border: '1px solid #d0dbe7' }}>
              <Typography sx={{ fontWeight: 700, mb: 2 }}>Finalizar Pesaje</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select fullWidth label="Responsable" value={responsible}
                    onChange={(e) => setResponsible(e.target.value)} size="small"
                  >
                    {users.map(u => <MenuItem key={u.id} value={u.id}>{u.first_name || u.username}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    type="password" fullWidth label="Contraseña" value={password}
                    onChange={(e) => setPassword(e.target.value)} size="small"
                  />
                </Grid>
              </Grid>
              <Button
                variant="contained" fullWidth sx={{ mt: 2, bgcolor: '#800020' }}
                onClick={handleFinish} disabled={saving || unweighedItemsCount > 0}
              >
                {saving ? <CircularProgress size={24} color="inherit" /> : "Marcar como Listo para Facturar"}
              </Button>
            </Paper>
          )}
        </Grid>

        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Paper sx={{ p: 2, bgcolor: '#f6f7f8' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Estado Actual</Typography>
              <StatusChip status={order?.status} />
            </Paper>
            <OrderTraceback order={order} />
          </Box>
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default ProductionDetail;
